import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { usePerformanceMonitoring } from '@/components/shared/PerformanceAndPolish';
import type { Tables } from '@/integrations/supabase/types';

type Node = Tables<'nodes'>;

interface NodesOptions {
  discipline?: string;
  status?: string;
  limit?: number;
  offset?: number;
  includeDisciplineSlug?: boolean;
}

export const useNodes = (
  discipline?: string, 
  status?: string, 
  options: Omit<NodesOptions, 'discipline' | 'status'> = {}
) => {
  const { limit = 50, offset = 0, includeDisciplineSlug = false } = options;
  const { updateMetrics } = usePerformanceMonitoring();
  
  return useQuery({
    queryKey: ['nodes', discipline, status, limit, offset],
    queryFn: async () => {
      const startTime = performance.now();
      
      let query = supabase
        .from('nodes')
        .select(`
          *,
          profiles!author_id(username, full_name, avatar_url),
          disciplines(name, slug)
        `)
        .order('updated_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (discipline) {
        if (includeDisciplineSlug) {
          // When filtering by discipline slug, we need to join properly
          query = query.eq('disciplines.slug', discipline);
        } else {
          // When filtering by discipline ID
          query = query.eq('discipline_id', discipline);
        }
      }

      if (status) {
        query = query.eq('status', status);
      }

      const { data, error } = await query;
      
      // Update performance metrics
      const queryTime = performance.now() - startTime;
      updateMetrics({ queryTime });
      
      if (error) {
        console.error('Error fetching nodes:', error);
        updateMetrics({ errorRate: 1 });
        throw error;
      }
      return data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
  });
};

export const useNode = (id: string) => {
  const { updateMetrics } = usePerformanceMonitoring();
  
  return useQuery({
    queryKey: ['node', id],
    queryFn: async () => {
      const startTime = performance.now();
      
      const { data, error } = await supabase
        .from('nodes')
        .select(`
          *,
          profiles!author_id(username, full_name, avatar_url),
          disciplines(name, slug),
          node_tags(
            tags(name, description)
          )
        `)
        .eq('id', id)
        .single();

      // Update performance metrics
      const queryTime = performance.now() - startTime;
      updateMetrics({ queryTime });

      if (error) {
        console.error('Error fetching node:', error);
        updateMetrics({ errorRate: 1 });
        throw error;
      }
      return data;
    },
    enabled: !!id,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// New optimized search hook
export const useNodesSearch = (searchQuery: string, filters: any = {}) => {
  const { updateMetrics } = usePerformanceMonitoring();
  
  return useQuery({
    queryKey: ['nodes-search', searchQuery, filters],
    queryFn: async () => {
      const startTime = performance.now();
      
      let query = supabase
        .from('nodes')
        .select(`
          id, title, content, node_type, status,
          disciplines(name, slug),
          profiles!author_id(username, full_name)
        `)
        .eq('status', 'published')
        .order('updated_at', { ascending: false })
        .limit(20);

      // Apply text search if query provided
      if (searchQuery) {
        query = query.textSearch('title,content', searchQuery);
      }

      // Apply filters
      if (filters.discipline_id) {
        query = query.eq('discipline_id', filters.discipline_id);
      }
      if (filters.node_type) {
        query = query.eq('node_type', filters.node_type);
      }

      const { data, error } = await query;
      
      const queryTime = performance.now() - startTime;
      updateMetrics({ queryTime });

      if (error) {
        console.error('Error searching nodes:', error);
        updateMetrics({ errorRate: 1 });
        throw error;
      }
      
      return data || [];
    },
    enabled: searchQuery.length >= 2,
    staleTime: 3 * 60 * 1000, // 3 minutes
    retry: 2
  });
};

// Optimized bulk content preloader
export const useContentPreloader = (nodeIds: string[]) => {
  const { updateMetrics } = usePerformanceMonitoring();
  
  return useQuery({
    queryKey: ['bulk-nodes', nodeIds.join(',')],
    queryFn: async () => {
      const startTime = performance.now();
      
      const { data, error } = await supabase
        .from('nodes')
        .select(`
          id, title, content, node_type,
          disciplines(name, slug),
          profiles!author_id(username, full_name)
        `)
        .in('id', nodeIds)
        .eq('status', 'published');

      const queryTime = performance.now() - startTime;
      updateMetrics({ queryTime, loadTime: queryTime });

      if (error) {
        console.error('Error preloading content:', error);
        updateMetrics({ errorRate: 1 });
        throw error;
      }
      
      return data || [];
    },
    enabled: nodeIds.length > 0,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 2
  });
};
